/**
 * ActivityRequestsPage - Onay bekleyen saha aktivitesi taleplerini listeler;
 * yönetici/admin burada sırası gelen talepleri onaylar veya reddeder.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, Button, Card, Empty, Form, Input, Modal, Popconfirm, Space, Table, Tag, Typography, message } from 'antd';
import { CarOutlined, UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { all_routes } from '../../../router/all_routes';
import fieldActivityService from '../../../services/field-activity/field-activity.service';
import { ActivityType, PendingFieldActivity } from '../../../types/field.types';
import { VehicleAssignmentType } from '../../../types/vehicle.types';
import { handleApiError } from '../../../utils/errorHandler';
import { getFileUrl } from '../../../utils/file.utils';
import ApprovalFlowTags from '../../shared/approval/ApprovalFlowTags';
import FooterSection from '../../shared/components/footerSection';

const { Text } = Typography;
const { TextArea } = Input;

const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  [ActivityType.VISIT]: 'Ziyaret',
  [ActivityType.DELIVERY]: 'Teslimat',
  [ActivityType.MAINTENANCE]: 'Bakım',
  [ActivityType.OFFICE]: 'Ofis',
  [ActivityType.OTHER]: 'Diğer',
};

const formatDateRange = (startDate: string, endDate?: string | null) => {
  const start = dayjs(startDate);
  if (!endDate) return start.format('DD.MM.YYYY HH:mm');
  const end = dayjs(endDate);
  if (start.isSame(end, 'day')) {
    return `${start.format('DD.MM.YYYY')} ${start.format('HH:mm')} - ${end.format('HH:mm')}`;
  }
  return `${start.format('DD.MM.YYYY HH:mm')} - ${end.format('DD.MM.YYYY HH:mm')}`;
};

const ActivityRequestsPage: React.FC = () => {
  const { t } = useTranslation();
  const routes = all_routes;
  const [rejectForm] = Form.useForm();

  const [activities, setActivities] = useState<PendingFieldActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState<number | null>(null);
  const [rejectTarget, setRejectTarget] = useState<PendingFieldActivity | null>(null);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fieldActivityService.getPendingApprovals();
      setActivities(result || []);
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleApprove = async (activity: PendingFieldActivity) => {
    setActionId(activity.id);
    try {
      await fieldActivityService.reviewFieldActivity(activity.id, { action: 'approve' });
      message.success('Aktivite talebi onaylandı');
      fetchPending();
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    try {
      const { rejectionReason } = await rejectForm.validateFields();
      setActionId(rejectTarget.id);
      await fieldActivityService.reviewFieldActivity(rejectTarget.id, {
        action: 'reject',
        rejectionReason: rejectionReason.trim(),
      });
      message.success('Aktivite talebi reddedildi');
      setRejectTarget(null);
      rejectForm.resetFields();
      fetchPending();
    } catch (error: any) {
      // Form validasyon hatalarında errorFields bulunur, mesaj formda gösteriliyor
      if (!error?.errorFields) handleApiError(error, t);
    } finally {
      setActionId(null);
    }
  };

  const columns = [
    {
      title: 'Çalışan',
      key: 'employee',
      render: (record: PendingFieldActivity) => {
        const user = record.assignedTo;
        if (!user) return <Text type="secondary">-</Text>;
        const picture = user.profilePicture && user.profilePicture.trim() !== '' ? getFileUrl(user.profilePicture) : undefined;
        return (
          <Space>
            <Avatar src={picture} icon={!picture ? <UserOutlined /> : undefined}>
              {!picture && `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`}
            </Avatar>
            <div>
              <div style={{ fontWeight: 500 }}>{user.firstName} {user.lastName}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {user.primaryDepartment?.name || 'Departman Yok'}
              </Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Aktivite',
      key: 'activity',
      render: (record: PendingFieldActivity) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.title}</div>
          <Tag style={{ marginTop: 4 }}>{ACTIVITY_TYPE_LABELS[record.type] || record.type}</Tag>
          {record.description && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>{record.description}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Tarih',
      key: 'dates',
      render: (record: PendingFieldActivity) => formatDateRange(record.startDate, record.endDate),
    },
    {
      title: 'Araç',
      key: 'vehicle',
      render: (record: PendingFieldActivity) =>
        record.vehicle ? (
          <Space size={4} wrap>
            <CarOutlined style={{ color: '#1890ff' }} />
            <span>{record.vehicle.name}</span>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.vehicle.plateNumber}</Text>
            {record.vehicle.assignmentType === VehicleAssignmentType.POOL && <Tag color="purple">Havuz</Tag>}
          </Space>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Onay Akışı',
      key: 'approvalFlow',
      render: (record: PendingFieldActivity) =>
        record.approvalFlow && record.approvalFlow.length > 0 ? (
          <ApprovalFlowTags steps={record.approvalFlow} />
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>Tek adımlı onay</Text>
        ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (record: PendingFieldActivity) => (
        <Space>
          <Popconfirm
            title="Bu aktivite talebini onaylamak istediğinizden emin misiniz?"
            onConfirm={() => handleApprove(record)}
            okText="Evet, Onayla"
            cancelText="İptal"
          >
            <Button type="primary" size="small" loading={actionId === record.id && !rejectTarget}>
              Onayla
            </Button>
          </Popconfirm>
          <Button
            danger
            size="small"
            disabled={actionId === record.id}
            onClick={() => {
              rejectForm.resetFields();
              setRejectTarget(record);
            }}
          >
            Reddet
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
      <div className="content flex-fill h-100">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Aktivite Talepleri</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Saha Yönetimi</li>
                <li className="breadcrumb-item active">Aktivite Talepleri</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <Button icon={<i className="ti ti-reload me-2" />} onClick={fetchPending} loading={loading}>
              Yenile
            </Button>
          </div>
        </div>

        <Card>
          <Table
            columns={columns}
            dataSource={activities}
            rowKey="id"
            loading={loading}
            scroll={{ x: true }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Onayınızı bekleyen aktivite talebi bulunmuyor"
                />
              ),
            }}
            pagination={{
              pageSize: 10,
              hideOnSinglePage: true,
              showTotal: (total) => `Toplam ${total} talep`,
            }}
          />
        </Card>
      </div>

      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
        <FooterSection />
      </div>

      <Modal
        title="Aktivite Talebini Reddet"
        open={!!rejectTarget}
        onOk={handleReject}
        onCancel={() => {
          setRejectTarget(null);
          rejectForm.resetFields();
        }}
        okText="Reddet"
        cancelText="İptal"
        okButtonProps={{ danger: true }}
        confirmLoading={!!rejectTarget && actionId === rejectTarget.id}
        centered
      >
        {rejectTarget && (
          <div className="mb-3 pb-3 border-bottom">
            <div>
              <Text type="secondary">Çalışan: </Text>
              <Text strong>{rejectTarget.assignedTo?.firstName} {rejectTarget.assignedTo?.lastName}</Text>
            </div>
            <div>
              <Text type="secondary">Aktivite: </Text>
              <Text strong>{rejectTarget.title}</Text>
            </div>
          </div>
        )}
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="rejectionReason"
            label="Red Nedeni"
            rules={[
              { required: true, whitespace: true, message: 'Red nedeni gerekli' },
              { max: 500, message: 'Red nedeni en fazla 500 karakter olabilir' },
            ]}
          >
            <TextArea rows={4} maxLength={500} showCount placeholder="Red nedenini yazın..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ActivityRequestsPage;
